import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import maplibregl, { Map } from "maplibre-gl";
import { Position } from "geojson";

import { useGetSettings } from "@/api/configuration/useConfigurationAPI";
import {
  MARKER_FOCUS_ZOOM_FOCUS,
  MARKER_FOCUS_ZOOM_LEVEL,
  MARKER_LABEL_CLASS,
  MARKER_RANK_CLASS
} from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  addPlaceCoordinateTemp,
  updateCoordinateItemTemp
} from "@/store/slice/spatialSlice";
import { setMarkerInfo, toggleAllowPinMarker } from "@/store/slice/uiSlice";
import { PlaceItem } from "@/types/place";
import {
  AreaInputType,
  CatchmentInputBy,
  ECatchmentInputBy
} from "@/types/spatial";
import { withBasePath } from "@/utils/basePath";
import {
  createMarkersFromDataset,
  getRenderableCatchmentDatasets
} from "@/utils/catchment";
import { ensureMapStyleReady, truncateGeoJson } from "@/utils/map";
import {
  CatchmentMarkerFeatureInput,
  removeCatchmentMarkers,
  renderCatchmentMarkers,
  setCatchmentMarkerLabelsVisibility,
  setCatchmentMarkersVisibility
} from "@/utils/map/catchmentMarker";

import { point } from "@turf/turf";
import { v4 as uuidv4 } from "uuid";

type UseMapMarkersProps = {
  map: Map;
  isMapLoaded: boolean;
};

const markerIcon = withBasePath("icons/marker.svg");

export const useMapMarkers = ({ map, isMapLoaded }: UseMapMarkersProps) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const {
    catchmentSettings,
    spatialConfig: { areaInput: spatialAreaInputType },
    catchmentSettingsTemp,
    isShowMarkerLabel
  } = useSelector((state: RootState) => state.spatial);
  const {
    showCatchmentPopup,
    allowPinMarker,
    showAnalysisResultPopup,
    showComparisonPopup,
    showAnalysisSummaryPopup,
    showSearchLocationPopup,
    isDrawPolygon
  } = useSelector((state: RootState) => state.ui);

  const isPreviewMode = searchParams.get("preview") === "true";
  const { isBVTDataForCatchmentEnabled } = useAppFeatures();
  const { data: analysisSettings } = useGetSettings();
  // @dalladirosa TODO: Create this marker because the current markers has too much side effect, make it hard to manage
  const catchmentMarkers = useRef<maplibregl.Marker[]>([]);
  const markers = useRef<maplibregl.Marker[]>([]);
  const seachLocationMarkers = useRef<Record<string, maplibregl.Marker>>({});

  const markerLabelPosition = "32px";
  const markerLabelStyle = `
    position: absolute;
    padding: 2px 4px;
    border-radius: 4px;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    bottom: ${markerLabelPosition};
    left: 50%;
    transform: translateX(-50%);
    pointer-events: none;
    width: 100px;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
    min-height: 2.4em;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `;

  const markerLabelRankPosition = "48px";
  const markerLabelRankStyle = `
    position: absolute;
    padding: 2px 4px;
    border-radius: 4px;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    bottom: ${markerLabelRankPosition};
    left: 50%;
    transform: translateX(-50%);
    pointer-events: none;
    width: 100px;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    line-height: 1.2;
    min-height: 2.4em;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `;

  // Create custom marker element
  const createCustomMarkerElement = (locationName?: string, rank?: number) => {
    const el = document.createElement("div");
    el.style.backgroundImage = `url(${markerIcon})`;
    el.style.backgroundSize = "cover";
    el.style.width = "27px";
    el.style.height = "41px";

    if (locationName) {
      const label = document.createElement("div");
      label.className = MARKER_LABEL_CLASS;
      label.style.cssText = markerLabelStyle;
      label.textContent = locationName;
      label.title = locationName;
      el.appendChild(label);
    }

    if (rank) {
      const rankLabel = document.createElement("div");
      rankLabel.className = MARKER_RANK_CLASS;
      rankLabel.style.cssText = markerLabelRankStyle;
      rankLabel.style.bottom = !isShowMarkerLabel
        ? markerLabelPosition
        : markerLabelRankPosition;
      rankLabel.textContent = `#${rank}`;
      el.appendChild(rankLabel);
    }

    return el;
  };

  // Add new marker
  const createMarker = (
    lngLat: [number, number],
    draggable = false,
    markerId?: string,
    locationName?: string,
    rank?: number
  ) => {
    const truncatedPoint = truncateGeoJson(point(lngLat));

    const marker = new maplibregl.Marker({
      draggable,
      element: createCustomMarkerElement(locationName, rank),
      anchor: "bottom" // set anchor to 'bottom' instead of the default 'center'
    })
      .setLngLat(truncatedPoint.geometry.coordinates as [number, number])
      .addTo(map);

    // Add marker id
    if (markerId) marker._element.id = markerId;

    // Respect current label visibility setting for newly created markers
    if (!isShowMarkerLabel) {
      const labelEl = marker._element.querySelector<HTMLDivElement>(
        `.${MARKER_LABEL_CLASS}`
      );
      if (labelEl) labelEl.style.display = "none";
    }

    if (draggable) {
      marker.on("dragend", () => {
        const { lng, lat } = marker.getLngLat();
        const truncatePoint = truncateGeoJson(point([lng, lat]));

        // Check and update coordinate for marker
        if (marker._element.id) {
          dispatch(
            updateCoordinateItemTemp({
              lng: truncatePoint.geometry.coordinates[0],
              lat: truncatePoint.geometry.coordinates[1],
              id: marker._element.id
            })
          );
        }
      });
    }

    return marker;
  };
  // Add markers to map
  const handleAddMarkers = (
    data: { name?: string; coordinates: [number, number] | Position }[],
    draggable: boolean = true
  ): maplibregl.Marker[] => {
    if (!map) return [];

    const listMarker: maplibregl.Marker[] = data.map((item, index) =>
      createMarker(
        item.coordinates as [number, number],
        draggable,
        undefined,
        item?.name || `Marker #${index + 1}`
      )
    );

    catchmentMarkers.current = listMarker;

    return listMarker;
  };

  const handleAddMarker = (
    id: string,
    coordinates: [number, number] | Position,
    data: PlaceItem | string | null
  ) => {
    if (!map) return;
    if (seachLocationMarkers.current[id]) {
      seachLocationMarkers.current[id].remove();
      dispatch(setMarkerInfo(null));
    }
    const marker = new maplibregl.Marker({
      draggable: false,
      element: createCustomMarkerElement(),
      anchor: "bottom" // set anchor to 'bottom' instead of the default 'center'
    })
      .setLngLat(coordinates as [number, number])
      .addTo(map);
    marker._element.addEventListener("click", () => {
      dispatch(setMarkerInfo(data));
    });
    seachLocationMarkers.current[id] = marker;
    map.flyTo({
      center: [coordinates[0], coordinates[1]], // or [longitude, latitude] directly
      zoom: MARKER_FOCUS_ZOOM_LEVEL, // Adjust zoom level as needed
      duration: 2000 // Animation duration in milliseconds (optional)
    });
  };

  const handleRemoveMarkerById = (id: string) => {
    if (seachLocationMarkers.current[id]) {
      seachLocationMarkers.current[id].remove();
      delete seachLocationMarkers.current[id];
    }
  };

  const handleRemoveMarker = (index: number) => {
    if (index < 0 || index >= markers.current.length) return;
    markers.current[index].remove();
    markers.current.splice(index, 1);
    // Radius circles are now a single merged GeoJSON source owned by
    // useCatchmentArea, which rebuilds it whenever placeCoordinates change (this
    // removal path always follows such a change) — so there is no per-marker
    // circle source to tear down here anymore.
  };

  const removeAllCatchmentMarkers = () => {
    if (catchmentMarkers.current.length > 0) {
      catchmentMarkers.current.forEach((item, index) => {
        handleRemoveMarker(index);
        item.remove();
      });
      catchmentMarkers.current = [];
    }
  };

  /** Remove only the DOM `Marker`s, leaving the GL dataset-marker source/layers intact */
  const removeDomMarkers = () => {
    if (markers.current.length) {
      markers.current.forEach((item) => item.remove());
      markers.current = [];
    }
  };

  const removeAllMarkers = () => {
    removeDomMarkers();
    removeCatchmentMarkers(map);
  };

  const hideAllMarkers = () => {
    if (catchmentMarkers.current.length) {
      catchmentMarkers.current.forEach((marker) => {
        const element = marker.getElement();
        if (element) {
          element.style.visibility = "hidden";
        }
      });
    }
    if (markers.current.length) {
      markers.current.forEach((marker) => {
        const element = marker.getElement();
        if (element) {
          element.style.visibility = "hidden";
        }
      });
    }

    setCatchmentMarkersVisibility(map, false, isShowMarkerLabel);
  };

  const showAllMarkers = () => {
    if (catchmentMarkers.current.length) {
      catchmentMarkers.current.forEach((marker) => {
        const element = marker.getElement();
        if (element) {
          element.style.visibility = "visible";
        }
      });
    }
    if (markers.current.length) {
      markers.current.forEach((marker) => {
        const element = marker.getElement();
        if (element) {
          element.style.visibility = "visible";
        }
      });
    }
    setCatchmentMarkersVisibility(map, true, isShowMarkerLabel);
  };

  const setMarkerLabelsVisibility = (visible: boolean) => {
    const toggle = (marker: maplibregl.Marker) => {
      const element = marker.getElement();
      if (!element) return;
      const label = element.querySelector<HTMLDivElement>(
        `.${MARKER_LABEL_CLASS}`
      );
      if (label) label.style.display = visible ? "" : "none";
    };

    if (catchmentMarkers.current.length) {
      catchmentMarkers.current.forEach(toggle);
    }
    if (markers.current.length) {
      markers.current.forEach(toggle);
    }
  };

  const hideAllMarkerLabels = () => {
    setMarkerLabelsVisibility(false);
    setCatchmentMarkerLabelsVisibility(map, false);
  };
  const showAllMarkerLabels = () => {
    setMarkerLabelsVisibility(true);
    setCatchmentMarkerLabelsVisibility(map, true);
  };

  /**
   * Redraws catchment markers from the *draft* (temporary) settings while the
   * catchment popup is open — i.e. the live preview as the user edits.
   */
  const updateMarkerTemporityDisplay = (
    catchmentInputBy?: CatchmentInputBy
  ) => {
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    if (!isCatchment || !showCatchmentPopup) return;

    // `renderCatchmentMarkers` is idempotent, so a replay is safe.
    const retryUpdateMarkerTemporityDisplay = () =>
      updateMarkerTemporityDisplay(catchmentInputBy);
    if (!ensureMapStyleReady(map, retryUpdateMarkerTemporityDisplay)) return;

    const markerList: maplibregl.Marker[] = [];

    if (catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
      removeAllMarkers();
      const coordinatesTemp = catchmentSettingsTemp?.placeCoordinates ?? [];
      coordinatesTemp.forEach((item, index) => {
        // Draggable "by location" pins stay DOM markers — dragging needs a real element.
        const marker = createMarker(
          [item.lng, item.lat],
          true,
          item.id,
          item.locationName || `Marker #${index + 1}`
        );
        markerList.push(marker);
      });
    } else {
      removeDomMarkers();
      const datasets = catchmentSettingsTemp.datasetConfig?.datasets ?? {};
      const visibleEntries = getRenderableCatchmentDatasets(
        datasets,
        catchmentSettingsTemp.datasetConfig?.visualizationOrder,
        analysisSettings?.max_catchments
      );

      const markerFeatures: CatchmentMarkerFeatureInput[] = [];
      createMarkersFromDataset(
        visibleEntries,
        (label, coordinates, id, rank) => {
          markerFeatures.push({ id, coordinates, label, rank });
        }
      );
      renderCatchmentMarkers(
        map,
        markerIcon,
        markerFeatures,
        isShowMarkerLabel
      );
    }
    markers.current = markerList;
  };

  /**
   * Redraws catchment markers from the *committed* (applied) settings when the
   * catchment popup is closed — i.e. the confirmed markers shown on the map.
   */
  const updateMarkerDisplay = (catchmentInputBy?: CatchmentInputBy) => {
    const isCatchment = spatialAreaInputType?.value === AreaInputType.CATCHMENT;

    const retryUpdateMarkerDisplay = () =>
      updateMarkerDisplay(catchmentInputBy);
    if (!ensureMapStyleReady(map, retryUpdateMarkerDisplay)) return;

    if (
      !isCatchment ||
      showCatchmentPopup ||
      showAnalysisResultPopup ||
      showComparisonPopup.isShow ||
      showAnalysisSummaryPopup
    )
      return;

    const markerList: maplibregl.Marker[] = [];

    if (catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
      removeAllMarkers();
      const coordinates = catchmentSettings?.placeCoordinates ?? [];
      coordinates.forEach((item, index) => {
        const marker = createMarker(
          [item.lng, item.lat],
          false,
          undefined,
          item.locationName || `Marker #${index + 1}`
        );
        markerList.push(marker);
      });
    } else {
      removeDomMarkers();
      const datasets = catchmentSettings?.datasetConfig?.datasets ?? {};
      const markerFeatures: CatchmentMarkerFeatureInput[] = [];
      const visibleEntries = getRenderableCatchmentDatasets(
        datasets,
        catchmentSettings?.datasetConfig?.visualizationOrder,
        analysisSettings?.max_catchments
      );
      createMarkersFromDataset(
        visibleEntries,
        (label, coordinates, id, rank) => {
          markerFeatures.push({ id, coordinates, label, rank });
        }
      );
      renderCatchmentMarkers(
        map,
        markerIcon,
        markerFeatures,
        isShowMarkerLabel
      );
    }

    markers.current = markerList;
  };

  const onDropMarkerOnMap = (e: maplibregl.MapLayerMouseEvent) => {
    const { lng, lat } = e.lngLat.wrap();

    const coordinates = truncateGeoJson(point([lng, lat]));

    dispatch(
      addPlaceCoordinateTemp({
        lng: coordinates.geometry.coordinates[0],
        lat: coordinates.geometry.coordinates[1],
        locationName: "",
        id: uuidv4()
      })
    );

    dispatch(toggleAllowPinMarker(false));

    map.flyTo({
      center: [
        coordinates.geometry.coordinates[0],
        coordinates.geometry.coordinates[1]
      ],
      zoom: MARKER_FOCUS_ZOOM_FOCUS
    });
  };

  const handleAllowPinMarkerChange = useCallback(
    (allowPinMarker: boolean | undefined) => {
      if (!map) return;
      const canvas = map.getCanvas();
      if (allowPinMarker) {
        canvas.style.cursor = `url(${markerIcon}) 13.5 20.5, auto`;
        map.on("click", onDropMarkerOnMap);
      } else {
        canvas.style.cursor = "default";
        map.off("click", onDropMarkerOnMap);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map]
  );

  useEffect(() => {
    handleAllowPinMarkerChange(allowPinMarker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowPinMarker]);

  useEffect(() => {
    if (!isMapLoaded || !map) return;
    if (isBVTDataForCatchmentEnabled && isDrawPolygon) {
      removeAllMarkers();
      return;
    }
    updateMarkerTemporityDisplay(catchmentSettingsTemp?.catchmentInputBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMapLoaded,
    showCatchmentPopup,
    catchmentSettingsTemp?.placeCoordinates,
    catchmentSettingsTemp?.catchmentInputBy,
    catchmentSettingsTemp.datasetConfig?.datasets,
    catchmentSettingsTemp.radiusData,
    spatialAreaInputType,
    isDrawPolygon
  ]);

  // Check if the map on preview mode will ignore add marker
  useEffect(() => {
    if (!isMapLoaded || !map || isPreviewMode || showSearchLocationPopup)
      return;
    if (isBVTDataForCatchmentEnabled && isDrawPolygon) {
      removeAllMarkers();
      return;
    }
    updateMarkerDisplay(catchmentSettings?.catchmentInputBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreviewMode,
    isMapLoaded,
    showCatchmentPopup,
    catchmentSettings?.placeCoordinates,
    catchmentSettings?.radiusData,
    spatialAreaInputType,
    showAnalysisResultPopup,
    showComparisonPopup,
    showSearchLocationPopup,
    catchmentSettings?.catchmentInputBy,
    catchmentSettings?.datasetConfig?.datasets,
    isDrawPolygon,
    analysisSettings?.max_catchments
  ]);

  return {
    markers: markers.current,
    handleAddMarkers,
    handleRemoveMarker,
    removeAllCatchmentMarkers,
    removeAllMarkers,
    hideAllMarkers,
    showAllMarkers,
    handleAddMarker,
    handleRemoveMarkerById,
    hideAllMarkerLabels,
    showAllMarkerLabels
  };
};

export default useMapMarkers;
